/* -- Progress component --------------------------------------------- */
// The bar itself is the native <progress> (CSS paints it). This module adds
// what HTML can't: live value readouts (every <output class="progress-value"
// for="id">, as a percent, a fraction "x / n" or a template), the data-level
// the auto tone reads, linear interpolation toward a new value, the
// declarative button commands (commandfor + command="--reset" …) and the
// named State API (AGENTS.md "State API").

// Shared preamble (AGENTS.md "State API"); the implementation lives in core.js —
// build.ts rewrites this import into a df$.shadcn.shared binding in dist/.
import { defussGlobals, defussQuery, componentState, bindComponent, textLocale } from '../../../shared/state-api.js';

const df$ = defussGlobals();
const dfDollar = defussQuery();

// VERIFIED: (verify's component types ratchet - tsc -p tsconfig.components.json) every type
// this file's API docs state - arguments, return values, event details - holds
// against its code: a wrong one is a new type error and fails the build.

/** What progress:change carries. */
interface ProgressChangeDetail {
  /** the <progress> value */
  value: number;
  /** its max */
  max: number;
  /** value / max, 0 to 1 */
  percent: number;
}

/** default = determinate at a value (as authored, or config.value);
 * indeterminate = no value (the moving sweep); complete = value == max. */
const progressStates = ['default', 'indeterminate', 'complete'];

/** setState() configs per state (getState() reports value, max and the fraction done). */
export interface ProgressStateConfigs {
  /** Determinate; no config restores the authored value. */
  default: {
    /** the value to show (clamped to 0..max); getState() reports it */
    value?: number | null;
    /** ms to glide there linearly (default: jump) */
    duration?: number;
    /** a new total; getState() reports it */
    max?: number;
    /** reported by getState(): value / max, 0 to 1 (null while indeterminate) */
    percent?: number | null;
  };
  /** No value - the moving sweep. */
  indeterminate: {
    /** reported by getState(): null */
    value?: number | null;
    /** reported by getState(): the total */
    max?: number;
    /** reported by getState(): value / max, 0 to 1 (null while indeterminate) */
    percent?: number | null;
  };
  /** The value at max - progress:completed fires. */
  complete: {
    /** ms to glide to max (default: jump) */
    duration?: number;
    /** a new total */
    max?: number;
    /** reported by getState(): max */
    value?: number | null;
    /** reported by getState(): value / max, 0 to 1 (null while indeterminate) */
    percent?: number | null;
  };
}

const SELECTOR = 'progress.progress';
const reducedMotion = () => globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

const maxOf = (el) => el.max || 1;
const clamp = (el, v) => Math.max(0, Math.min(maxOf(el), Number(v) || 0));
/** Whole numbers stay whole; fractions keep one decimal (a tween passes 42.7). */
const round = (v) => Math.round(v * 10) / 10;

/** formatters in the text's locale (nearest [lang], else 'en') - never the browser's; one pair per locale */
const formats = new Map();
const fmtFor = (node) => {
  const locale = textLocale(node);
  if (!formats.has(locale)) {
    formats.set(locale, {
      pct: new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 0 }),
      num: new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }),
    });
  }
  return formats.get(locale);
};

/** The readout text for one output (data-format / data-template on the output). */
function text(out, el) {
  const { pct: pctFmt, num: numFmt } = fmtFor(out);
  const v = el.position < 0 ? null : el.value;
  const max = maxOf(el);
  if (v == null) return out.dataset.indeterminate ?? '…';
  const pct = v / max;
  const tpl = out.dataset.template;
  if (tpl) {
    return tpl
      .replaceAll('{value}', numFmt.format(Math.round(v)))
      .replaceAll('{max}', numFmt.format(max))
      .replaceAll('{percent}', pctFmt.format(pct));
  }
  switch (out.dataset.format) {
    case 'fraction':
      return `${numFmt.format(Math.round(v))} / ${numFmt.format(max)}`;
    case 'value':
      return numFmt.format(Math.round(v));
    default:
      return pctFmt.format(pct);
  }
}

/** Outputs bound to a bar: output[for~=id] anywhere, plus .progress-value in its field. */
function outputsOf(el) {
  const outs = new Set<HTMLOutputElement>();
  if (el.id)
    dfDollar<HTMLOutputElement>(`output.progress-value[for~="${CSS.escape(el.id)}"]`)
      .toArray()
      .forEach((o) => outs.add(o));
  dfDollar(el)
    .closest('.progress-field')
    .find<HTMLOutputElement>('.progress-value')
    .toArray()
    .forEach((o) => {
      if (!o.htmlFor?.value || (el.id && o.htmlFor.contains(el.id))) outs.add(o);
    });
  return [...outs];
}

/** Paint everything derived from the value: level, complete flag, readouts, aria-valuetext. */
function paint(el) {
  const indeterminate = el.position < 0;
  const pct = indeterminate ? 0 : el.value / maxOf(el);
  el.dataset.level = pct < 0.34 ? 'low' : pct < 0.67 ? 'mid' : 'high';
  el.toggleAttribute('data-complete', !indeterminate && el.value >= maxOf(el));
  let spoken = '';
  for (const out of outputsOf(el)) {
    const t = text(out, el);
    out.value = t;
    out.dataset.text = t;
    out.style.setProperty('--progress-pct', `${(pct * 100).toFixed(2)}%`);
    if (out.dataset.format === 'fraction' || out.dataset.template) spoken ||= t;
  }
  // a fraction / template is what the reader means - say it, not the percent
  if (spoken) el.setAttribute('aria-valuetext', spoken);
  else el.removeAttribute('aria-valuetext');
}

function stopTween(el) {
  if (el._raf) cancelAnimationFrame(el._raf);
  el._raf = 0;
}

/** Settle on a value: the state name follows it (max → complete), events fire. */
function commit(el, v, emit = true) {
  const before = el.dataset.stateName;
  el.value = v;
  paint(el);
  const done = v >= maxOf(el);
  el.dataset.stateName = done ? 'complete' : 'default';
  // Fires when the value changes - value, max and the fraction done (0 to 1).
  if (emit)
    el.dispatchEvent(
      new CustomEvent<ProgressChangeDetail>('progress:change', {
        bubbles: true,
        detail: { value: el.value, max: el.max, percent: el.value / maxOf(el) },
      }),
    );
  // Fires once when the value reaches max.
  if (done && before !== 'complete') el.dispatchEvent(new CustomEvent('progress:completed', { bubbles: true }));
}

/** Linear interpolation from the current value to `to` over `duration` ms
 * (time-based, so a throttled frame never slows the total). */
function tween(el, to, duration) {
  stopTween(el);
  const from = el.position < 0 ? 0 : el.value;
  if (!(duration > 0) || reducedMotion() || from === to) return commit(el, to);
  const t0 = performance.now();
  el.dataset.stateName = 'default';
  el.toggleAttribute('data-running', true);
  const frame = (now) => {
    const k = Math.min(1, (now - t0) / duration);
    if (k < 1) {
      el.value = round(from + (to - from) * k);
      paint(el);
      el._raf = requestAnimationFrame(frame);
    } else {
      el._raf = 0;
      el.removeAttribute('data-running');
      commit(el, to);
    }
  };
  el._raf = requestAnimationFrame(frame);
}

const stepOf = (el) => parseFloat(el.dataset.step || '') || maxOf(el) / 10;
const durationOf = (el) => parseFloat(el.dataset.duration || '') || 3000;

/**
 * The markup of a state, for render(), on a detached copy of the authored
 * <progress>: its value (the attribute) and the flags paint() derives -
 * level, complete, the spoken fraction / template. paint() itself also
 * writes the page's <output> readouts, so it never runs on a copy: the
 * readouts are only READ here (the live element's, found by id).
 */
function applyMarkup(el, stateName, config) {
  const authored = el.position < 0 ? null : el.value;
  if (config?.max != null && Number(config.max) !== el.max) el.max = Number(config.max);
  if (stateName === 'indeterminate') dfDollar(el).attr('value', null);
  else
    el.value = stateName === 'complete' ? maxOf(el) : clamp(el, config?.value != null ? config.value : (authored ?? 0));
  const indeterminate = el.position < 0;
  const pct = indeterminate ? 0 : el.value / maxOf(el);
  dfDollar(el).attr('data-level', pct < 0.34 ? 'low' : pct < 0.67 ? 'mid' : 'high');
  dfDollar(el).attr('data-complete', !indeterminate && el.value >= maxOf(el) ? '' : null);
  const live = el.id ? dfDollar('#' + CSS.escape(el.id)).get(0) : null;
  const spoken = (live ? outputsOf(live) : [])
    .filter((out) => out.dataset.format === 'fraction' || out.dataset.template)
    .map((out) => text(out, el))[0];
  dfDollar(el).attr('aria-valuetext', spoken || null);
}

function triggerStateChange(el, stateName, config) {
  switch (stateName) {
    case 'default': {
      // only a max that differs is written: setState(getState()) changes nothing
      if (config.max != null && Number(config.max) !== el.max) el.max = Number(config.max);
      const to = config.value != null ? clamp(el, config.value) : clamp(el, el._authored ?? 0);
      if (config.duration > 0) tween(el, to, config.duration);
      else {
        stopTween(el);
        el.removeAttribute('data-running');
        commit(el, to);
      }
      break;
    }
    case 'indeterminate':
      stopTween(el);
      el.removeAttribute('data-running');
      el.removeAttribute('value');
      paint(el);
      el.dataset.stateName = 'indeterminate';
      break;
    case 'complete':
      if (config.duration > 0) tween(el, maxOf(el), config.duration);
      else {
        stopTween(el);
        el.removeAttribute('data-running');
        commit(el, maxOf(el));
      }
      break;
  }
}

/** Registry-level API; pass the <progress class="progress"> explicitly. Unknown names throw. */
export const progressApi = componentState<HTMLProgressElement>({
  component: 'progress',
  states: progressStates,
  apply: (el, state) => triggerStateChange(el, state.name, state.config),
  read: (el, state) => {
    const indeterminate = el.position < 0;
    return {
      name: el.dataset.stateName || 'default',
      config: {
        ...state.config,
        value: indeterminate ? null : el.value,
        max: el.max,
        percent: indeterminate ? null : el.value / maxOf(el),
      },
    };
  },
  markup: (el, state) => applyMarkup(el, state.name, state.config),
});

df$.progressApi = progressApi;
df$.progressStates = progressStates;

/** The command vocabulary (commandfor="bar-id" command="--…", or an event
 * progress:<name> dispatched on the bar). */
function run(el, command) {
  const now = el.position < 0 ? 0 : el.value;
  switch (command) {
    case 'reset':
      progressApi.setState(el, 'default', { value: 0 });
      break;
    case 'increment':
      progressApi.setState(el, 'default', { value: now + stepOf(el) });
      break;
    case 'decrement':
      progressApi.setState(el, 'default', { value: now - stepOf(el) });
      break;
    case 'complete':
      progressApi.setState(el, 'complete');
      break;
    case 'indeterminate':
      progressApi.setState(el, 'indeterminate');
      break;
    // linear run from here to max over data-duration (remaining share of it)
    case 'play': {
      // a full bar starts over from 0
      if (now >= maxOf(el)) el.value = 0;
      const rest = 1 - (el.position < 0 ? 0 : el.value) / maxOf(el);
      progressApi.setState(el, 'default', { value: maxOf(el), duration: durationOf(el) * rest });
      break;
    }
    case 'pause':
      stopTween(el);
      el.removeAttribute('data-running');
      commit(el, el.value);
      break;
    default:
      return false;
  }
  return true;
}
const COMMANDS = ['reset', 'increment', 'decrement', 'complete', 'indeterminate', 'play', 'pause'];

function init() {
  dfDollar<HTMLProgressElement>(`${SELECTOR}:not([data-init])`)
    .toArray()
    .forEach((el) => {
      el.dataset.init = '';
      // el.store + el.api (AGENTS.md "State through stores")
      bindComponent(el, progressApi);
      el._authored = el.position < 0 ? null : el.value;
      el.dataset.stateName = el.position < 0 ? 'indeterminate' : el.value >= maxOf(el) ? 'complete' : 'default';
      // Invoker Commands: <button commandfor="id" command="--reset">
      el.addEventListener('command', (e) => {
        const c = String((e as CommandEvent).command || '');
        if (c.startsWith('--')) run(el, c.slice(2));
      });
      for (const c of COMMANDS) el.addEventListener(`progress:${c}`, () => run(el, c));
      paint(el);
    });
}

// Browsers without the Invoker Commands API: the same buttons, by click.
if (!('commandForElement' in HTMLButtonElement.prototype) && !document.__progressCommandInit) {
  document.__progressCommandInit = true;
  document.addEventListener('click', (e) => {
    const btn = e.target instanceof Element ? e.target.closest('button[commandfor][command^="--"]') : null;
    const el = btn && dfDollar('#' + CSS.escape(btn.getAttribute('commandfor'))).get(0);
    if (el?.matches(`${SELECTOR}[data-init]`)) run(el, btn.getAttribute('command').slice(2));
  });
}

// A value / max written straight to the element (el.value = 40, or an
// attribute) repaints the readouts too.
new MutationObserver((records) => {
  for (const r of records) {
    const el = r.target;
    if (el instanceof HTMLProgressElement && el.matches(`${SELECTOR}[data-init]`) && !el._raf) {
      paint(el);
      el.dataset.stateName = el.position < 0 ? 'indeterminate' : el.value >= maxOf(el) ? 'complete' : 'default';
    }
  }
}).observe(document, { attributes: true, subtree: true, attributeFilter: ['value', 'max'] });

init();
new MutationObserver(init).observe(document, { childList: true, subtree: true });
